import Urls from 'Containers/Urls';
import { EMBEDDED_PARAMS, PAYMENT_ID, PAYMENT_INTENT_ID } from 'Src/consts/params';
import { createApplication } from 'Services/Applications/Application';
import { LoggerFactory, DOMAIN, FEATURE } from 'Services/Logger';
import {
  getReferrerInStoreFromLocalStorage,
  deleteReferrerInStoreFromLocalStorage,
} from 'Utils/referrerInStoreHelp';
import { navigateTo } from 'Utils/windowHelper';
import { utmCampaignToAcquisitionProduct } from 'Utils/mappers';
import { UtmQueryParameter, UtmAcquisitionProduct } from 'Models/Utm';
import { getLocalStorageItemWithTTL } from 'Utils/localStorageWithTTL';
import { deepLink } from 'Utils/deepLink';

const logger = LoggerFactory.getLogger(DOMAIN.FUNNEL);

interface ICreateKYCApplication {
  pushConnected: (url: string) => void;
  forceVerifyEmail?: boolean;
  userSource?: string
  isAmazonSignUpTrackingEnabled: boolean;
}

interface IStartApplication extends ICreateKYCApplication {
  isKnown?: boolean;
  isEmbeddedAmzApp?: boolean;
  paymentId?: string;
  paymentIntentId?: string;
}
/* eslint-disable camelcase */
type ApplicationProps = {
  acquisition_channel: string | null;
  acquisition_product: UtmAcquisitionProduct.PERSONAL_LOANS | null
  source_signup?: string
}


export const createPaymentApplication = async ({
  paymentId,
  paymentIntentId,
  isEmbeddedAmzApp,
  pushConnected,
}: IStartApplication) => {
  const payload = paymentIntentId
    ? { embedded_flow: isEmbeddedAmzApp, payment_intent_id: paymentIntentId }
    : { payment_id: paymentId };

  const paymentToLog = paymentIntentId || paymentId;
  logger.info(`Creating application with payment ${paymentToLog}`, `${FEATURE.FUNNEL}`);
  try {
    await createApplication(payload);
    logger.info(`Created application with payment ${paymentToLog}`, `${FEATURE.FUNNEL}`);

    if (isEmbeddedAmzApp) {
      logger.info(
        `[Embedded AMZ] Created application with payment ${paymentToLog}`,
        `${FEATURE.ACCOUNTS_MANAGEMENT}`);
    }

    pushConnected(Urls.apply + (isEmbeddedAmzApp ? EMBEDDED_PARAMS : ''));
  } catch (error) {
    logger.warn(
      `Failed to create application with payment ${paymentToLog}`,
      `${FEATURE.FUNNEL}`,
      { error });
    let redirectURL = `${Urls.createApplicationError}?${PAYMENT_INTENT_ID}=${paymentIntentId}`;
    if (!paymentIntentId) {
      redirectURL = `${Urls.createApplicationError}?${PAYMENT_ID}=${paymentId}`;
    }
    pushConnected(redirectURL);
  }
};

export const createKYCApplication = async ({
  forceVerifyEmail,
  pushConnected,
  userSource,
  isAmazonSignUpTrackingEnabled,
}: ICreateKYCApplication) => {
  logger.info('Creating KYC application', `${FEATURE.FUNNEL}`);
  const acquisitionProduct = utmCampaignToAcquisitionProduct(
    getLocalStorageItemWithTTL(UtmQueryParameter.CAMPAIGN) ?? '',
  );
  const acquisitionChannel = getLocalStorageItemWithTTL(UtmQueryParameter.SOURCE);
  const applicationProps: ApplicationProps = {
    acquisition_channel: acquisitionChannel,
    acquisition_product: acquisitionProduct,
  };
  if (isAmazonSignUpTrackingEnabled) {
    applicationProps.source_signup = userSource;
  }

  try {
    await createApplication(applicationProps);
    logger.info('Created KYC application', `${FEATURE.FUNNEL}`);
    if (forceVerifyEmail) pushConnected(`${Urls.verifyAccount}/?send_email=true`);
    else pushConnected(Urls.apply);
  } catch (error) {
    logger.warn('Failed to create KYC application', `${FEATURE.FUNNEL}`, { error });
  }
};

export default async function StartApplication(props: IStartApplication) {
  const {
    paymentId,
    paymentIntentId,
    pushConnected,
    isKnown,
  } = props;

  if (!paymentId && !paymentIntentId) {
    if (isKnown === false) {
      return createKYCApplication(props);
    }

    const referrer = getReferrerInStoreFromLocalStorage();
    deleteReferrerInStoreFromLocalStorage();
    if (referrer?.isInStore) {
      return navigateTo(referrer.value);
    }
    pushConnected(deepLink.get() ?? Urls.home);
  } else {
    return createPaymentApplication(props);
  }
}
